import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { backendApi } from "../../api";
import {
  IAlgorithmCreateResponse,
  IAlgorithmEditRequest,
  IAlgorithmEditResponse,
  IFormAlgorithmDB,
} from "./interfaces";
import { IAlgorithmCreateRequest } from "./interfaces";
import { enqueueSnackbar } from "notistack";
import { queryClient } from "../../queryClient";
import { isAxiosError } from "axios";

export function algorithmQuery(algorithmId: string) {
  return queryOptions({
    queryKey: ["algorithm", algorithmId],
    queryFn: async () =>
      (
        await backendApi<IFormAlgorithmDB>({
          url: `/algorithms/algorithm-form-detail/${algorithmId}`,
        })
      ).data,
    staleTime: 1000 * 3,
  });
}

export function fetchAlgorithm(algorithmId: string) {
  return queryClient.fetchQuery(algorithmQuery(algorithmId));
}

export function useGetAlgorithm(algorithmId: string) {
  return useQuery(algorithmQuery(algorithmId));
}

export function useCreateAlgorithm(
  onCreateSuccess?: (data: IAlgorithmCreateResponse) => void
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: IAlgorithmCreateRequest) =>
      (
        await backendApi<IAlgorithmCreateResponse>({
          url: "/algorithms/create-algorithm-form",
          method: "POST",
          data,
        })
      ).data,
    onSuccess: (data) => {
      queryClient.setQueryData(algorithmQuery(data.uuid).queryKey, data);
      if (onCreateSuccess) onCreateSuccess(data);
      enqueueSnackbar("New form created!", { variant: "success" });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 401) {
        localStorage.setItem(
          "next",
          window.location.pathname + window.location.search
        );
        window.location.reload();
      }
      enqueueSnackbar("Creating new form failed", { variant: "error" });
    },
  });
}

export function useEditAlgorithm(
  algorithmId: string,
  onEditSuccess?: (data: IAlgorithmEditResponse) => void
) {
  return useMutation({
    mutationFn: async (data: IAlgorithmEditRequest) =>
      (
        await backendApi<IAlgorithmEditResponse>({
          url: `/algorithms/algorithm-form-detail/${algorithmId}`,
          method: "PATCH",
          data,
        })
      ).data,
    onSuccess: (data) => {
      queryClient.setQueryData(algorithmQuery(data.uuid).queryKey, data);
      if (onEditSuccess) onEditSuccess(data);
      enqueueSnackbar("Edit successful", { variant: "success" });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 401) {
        localStorage.setItem(
          "next",
          window.location.pathname + window.location.search
        );
        window.location.reload();
      }
      enqueueSnackbar("Edit failed", { variant: "error" });
    },
  });
}

export function useDeleteAlgorithm(
  algorithmId: string,
  onDeleteSuccess?: () => void
) {
  return useMutation({
    mutationFn: async () =>
      await backendApi<null>({
        url: `/algorithms/algorithm-form-detail/${algorithmId}`,
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: algorithmQuery(algorithmId).queryKey,
      });
      if (onDeleteSuccess) onDeleteSuccess();
      enqueueSnackbar("Delete successful", { variant: "success" });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 401) {
        localStorage.setItem(
          "next",
          window.location.pathname + window.location.search
        );
        window.location.reload();
      }
      enqueueSnackbar("Deleting failed", { variant: "error" });
    },
  });
}
